import { Component, input, output, inject, ChangeDetectionStrategy, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuestionView, QuestionType } from '@formrails/form-builder/data-access';

/**
 * Base abstract class for all question components.
 * Provides common functionality and ensures consistent interface.
 * Uses Angular signals for reactive inputs and outputs.
 */
@Component({
  template: '', // Abstract component, no template
  changeDetection: ChangeDetectionStrategy.OnPush
})
export abstract class BaseQuestionComponent {
  // Signal inputs
  readonly question = input.required<QuestionView>();
  readonly isEditing = input(false);
  readonly disabled = input(false);

  // Signal outputs
  readonly questionChange = output<QuestionView>();
  readonly configChange = output<Partial<QuestionView>>();
  readonly delete = output<void>();

  protected sanitizer = inject(DomSanitizer);

  /**
   * Get the icon information for the current question type
   */
  getQuestionTypeIcon(): { icon: string; iconType: 'prime' | 'material' } | null {
    const q = this.question();
    if (!q.questionType) {
      return null;
    }

    const iconMap: Record<QuestionType, { icon: string; iconType: 'prime' | 'material' }> = {
      [QuestionType.CheckboxList]: { icon: 'pi-check-square', iconType: 'prime' },
      [QuestionType.DropdownList]: { icon: 'pi-chevron-down', iconType: 'prime' },
      [QuestionType.DynamicDropdown]: { icon: 'dynamic_feed', iconType: 'material' },
      [QuestionType.Email]: { icon: 'pi-at', iconType: 'prime' },
      [QuestionType.Date]: { icon: 'pi-calendar', iconType: 'prime' },
      [QuestionType.TextBox]: { icon: 'short_text', iconType: 'material' },
      [QuestionType.Tel]: { icon: 'pi-phone', iconType: 'prime' },
      [QuestionType.Radio]: { icon: 'pi-circle', iconType: 'prime' },
      [QuestionType.TextArea]: { icon: 'subject', iconType: 'material' },
      [QuestionType.Toggle]: { icon: 'toggle_on', iconType: 'material' },
      [QuestionType.Confirmation]: { icon: 'rule', iconType: 'material' },
      [QuestionType.HelperText]: { icon: 'help', iconType: 'material' },
      [QuestionType.Section]: { icon: 'border_horizontal', iconType: 'material' },
      [QuestionType.Title]: { icon: 'title', iconType: 'material' }
    };

    return iconMap[q.questionType] || null;
  }

  /**
   * Update the question label
   */
  protected updateLabel(labelText: string): void {
    this.emitQuestionChange({ ...this.question(), labelText });
  }

  /**
   * Update the question required status.
   * Keeps `isRequired` and `validators.required` in sync so downstream
   * consumers that key off the validators map (runtime renderer, backend
   * rule wiring) see the flag no matter which UI entry point toggled it.
   */
  protected updateRequired(isRequired: boolean): void {
    const current = this.question();
    const nextValidators = { ...(current.validators ?? {}) };
    if (isRequired) {
      // API contract: validators is a Dictionary<string, string> on the
      // server, so store the value as the string 'true' — not a boolean.
      nextValidators['required'] = 'true';
    } else {
      delete nextValidators['required'];
    }
    this.emitQuestionChange({
      ...current,
      isRequired,
      validators: nextValidators
    });
  }

  /**
   * Update the question helper text
   */
  protected updateHelperText(helperText: string): void {
    this.emitQuestionChange({ ...this.question(), helperText });
  }

  /**
   * Update the question additional info
   */
  protected updateAdditionalInfo(additionalInfo: string): void {
    this.emitQuestionChange({ ...this.question(), additionalInfo });
  }

  /**
   * Update question configuration
   */
  protected updateConfig(config: Partial<QuestionView>): void {
    this.emitQuestionChange({ ...this.question(), ...config });
  }

  /**
   * Emit question change event
   */
  protected emitQuestionChange(updatedQuestion: QuestionView): void {
    this.questionChange.emit(updatedQuestion);
  }

  /**
   * Emit configuration change event
   */
  protected emitConfigChange(config: Partial<QuestionView>): void {
    this.configChange.emit(config);
  }

  /**
   * Handle delete action
   */
  handleDelete(): void {
    this.delete.emit();
  }

  /**
   * Formatting a question's author put in its label or help text is kept; anything that could run
   * is stripped. The text comes from a stored template, so it is not this component's to trust:
   * bypassSecurityTrustHtml would mark it trusted and turn Angular's sanitiser off entirely,
   * leaving `<img src=x onerror=...>` to run in the session of whoever opened the form.
   *
   * Public so the question templates can call it from [innerHTML].
   */
  public sanitizeHtml(html: string | undefined | null): SafeHtml {
    if (!html) {
      return '';
    }

    return this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
  }
}

